import type { DocType } from '../../types/testdata.types'

export type DocumentNumberMode = 'auto' | 'manual'

/**
 * Pure decision rules for the autonumber ownership state machine
 * (`XMLInsights-SDD` `REQ-TESTDATA-001`). Kept dependency-free (no React
 * imports) so they can be unit-tested without a frontend test framework —
 * see `docNumberMode.test.ts` (runs via Node's built-in `node:test`, no new
 * devDependency).
 *
 * `useDocNumber.ts` is the only caller; it owns all React state/refs and
 * side effects (fetches, `setForm`). These functions never touch either.
 */

/**
 * Transition rule "user-managed -> auto-managed" (REQ-TESTDATA-001): only an
 * actual `docType` switch resets a field to auto-managed. A calendar-day
 * rollover, an `initials` change, or any other cause for a re-render MUST
 * NOT reset a manually-edited field — it stays exactly as the user left it.
 *
 * `prevDocType === null` means "no prior docType observed yet" (first
 * mount) — never counts as a switch.
 */
export function nextDocumentNumberMode(
  prevDocType: DocType | null,
  docType: DocType,
  currentMode: DocumentNumberMode,
): DocumentNumberMode {
  const docTypeChanged = prevDocType !== null && prevDocType !== docType
  return docTypeChanged ? 'auto' : currentMode
}

/**
 * Pre-submit / display-sync rule (REQ-TESTDATA-001): only an auto-managed
 * field is ever written to by a server-driven suggestion — a page-load or
 * cache-refresh update, or the pre-submit peek (both call this the same
 * predicate `useDocNumber.ts` uses to guard every `setForm` write and every
 * network call it triggers). A user-managed field is never touched and
 * never causes a network call.
 */
export function isAutoManaged(mode: DocumentNumberMode): boolean {
  return mode === 'auto'
}
